import { useMemo, useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Table2 } from "lucide-react";
import { useCollectionPerformance } from "../../api/queries/dashboardQueries";
import { displayCurrencyAmount } from "../../utils/display";
import { formatCollectionPeriodLabel } from "../../utils/periodGrouping";
import { CHROME, MARKS, SERIES } from "../../utils/receivablesChartTokens";

/**
 * Invoiced vs collected, period by period.
 *
 * Both series are money on one shared axis — never a second y-scale, and no
 * percentage line overlaid on a currency scale. The collection rate lives in
 * the subtitle and the tooltip instead, where it can't imply a false
 * correlation with the bar heights.
 */

type ManagerFilters = {
  collectorId?: number;
  customerGroupId?: number;
};

type Props = {
  currency: string;
  startDate?: string;
  endDate?: string;
  grouping: "month" | "quarter" | "year";
  managerFilters?: ManagerFilters;
  consolidated?: boolean;
};

type Point = {
  period: string;
  label: string;
  invoiced: number;
  collected: number;
  collectionRate: number;
};

function InvoicedTooltip({
  active,
  payload,
  currency,
}: {
  active?: boolean;
  payload?: ReadonlyArray<{ payload: Point }>;
  currency: string;
}) {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  const rows = [
    { label: "Invoiced", value: point.invoiced, color: SERIES.primary },
    { label: "Collected", value: point.collected, color: SERIES.secondary },
  ];
  return (
    <div className="rounded-md border border-[#E5E7EB] bg-white/95 px-3 py-2 text-xs shadow-lg backdrop-blur-sm">
      <p className="mb-1.5 font-medium text-[#111827]">{point.label}</p>
      {rows.map((row) => (
        <div key={row.label} className="flex justify-between gap-6">
          <span className="inline-flex items-center gap-1.5 text-[#52514E]">
            <span
              className="h-2 w-2 rounded-sm"
              style={{ backgroundColor: row.color }}
              aria-hidden
            />
            {row.label}
          </span>
          <span className="tabular-nums text-[#111827]">
            {displayCurrencyAmount(row.value, currency)}
          </span>
        </div>
      ))}
      <div className="mt-1.5 flex justify-between gap-6 border-t border-[#F3F4F6] pt-1.5">
        <span className="text-[#52514E]">Collection rate</span>
        <span className="font-medium tabular-nums text-[#111827]">
          {Math.round(point.collectionRate)}%
        </span>
      </div>
    </div>
  );
}

export function DashboardInvoicedVsCollectedChart({
  currency,
  startDate,
  endDate,
  grouping,
  managerFilters,
  consolidated,
}: Props) {
  const [showTable, setShowTable] = useState(false);

  const performanceQuery = useCollectionPerformance(
    currency,
    startDate,
    endDate,
    grouping,
    "issue_date",
    managerFilters,
    consolidated,
  );

  const points = useMemo<Point[]>(() => {
    const rows = performanceQuery.data?.performance ?? [];
    return rows.map((row) => {
      const invoiced = row.total_issued_amount ?? 0;
      const collected = row.collected_amount ?? 0;
      return {
        period: row.period,
        label: formatCollectionPeriodLabel(row.period, grouping),
        invoiced,
        collected,
        collectionRate: invoiced > 0 ? (collected / invoiced) * 100 : 0,
      };
    });
  }, [performanceQuery.data?.performance, grouping]);

  const totals = useMemo(() => {
    const invoiced = points.reduce((sum, point) => sum + point.invoiced, 0);
    const collected = points.reduce((sum, point) => sum + point.collected, 0);
    return {
      invoiced,
      collected,
      rate: invoiced > 0 ? (collected / invoiced) * 100 : 0,
    };
  }, [points]);

  if (performanceQuery.isError) {
    return (
      <div
        role="alert"
        className="rounded-xl border border-[#FECACA] bg-[#FEF2F2] px-4 py-3 text-sm text-[#DC2626]"
      >
        Unable to load invoiced vs collected.
      </div>
    );
  }

  if (performanceQuery.isPending) {
    return (
      <div className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm">
        <div className="mb-3 h-7 w-48 animate-pulse rounded bg-[#F3F4F6]" />
        <div className="h-72 w-full animate-pulse rounded-lg bg-[#F3F4F6]" />
      </div>
    );
  }

  return (
    <article
      aria-label="Invoiced versus collected by period"
      className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-sm"
    >
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-[#111827]">
            Invoiced vs collected
          </h3>
          <p className="mt-0.5 text-sm text-[#6B7280]">
            {totals.invoiced > 0
              ? `${Math.round(totals.rate)}% of invoiced value collected over the period`
              : "Billing and collection by period"}
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowTable((value) => !value)}
          aria-pressed={showTable}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[#E5E7EB] px-2.5 py-1.5 text-xs font-medium text-[#52514E] transition hover:bg-[#FAFAFA]"
        >
          <Table2 className="h-3.5 w-3.5" aria-hidden />
          {showTable ? "Show chart" : "Show table"}
        </button>
      </div>

      {points.length === 0 ? (
        <div className="flex h-72 flex-col items-center justify-center rounded-lg border border-dashed border-[#E5E7EB] bg-[#FAFAFA] px-6 text-center">
          <p className="text-sm font-medium text-[#111827]">No invoice activity</p>
          <p className="mt-1 text-sm text-[#6B7280]">
            No invoices issued in the selected period.
          </p>
        </div>
      ) : showTable ? (
        <div className="max-h-80 overflow-auto rounded-lg border border-[#E5E7EB]">
          <table className="w-full text-sm">
            <caption className="sr-only">
              Invoiced and collected amounts by period
            </caption>
            <thead className="sticky top-0 bg-[#FAFAFA] text-xs text-[#52514E]">
              <tr>
                <th scope="col" className="px-3 py-2 text-left font-medium">
                  Period
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  Invoiced
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  Collected
                </th>
                <th scope="col" className="px-3 py-2 text-right font-medium">
                  Rate
                </th>
              </tr>
            </thead>
            <tbody>
              {points.map((point) => (
                <tr key={point.period} className="border-t border-[#F3F4F6]">
                  <td className="px-3 py-1.5 text-[#111827]">{point.label}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-[#52514E]">
                    {displayCurrencyAmount(point.invoiced, currency)}
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-right tabular-nums text-[#52514E]">
                    {displayCurrencyAmount(point.collected, currency)}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-[#111827]">
                    {Math.round(point.collectionRate)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <>
          <div
            className="h-72 w-full"
            role="img"
            aria-label="Invoiced versus collected amounts by period"
          >
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart
                data={points}
                margin={{ top: 8, right: 12, bottom: 4, left: 4 }}
                barGap={2}
              >
                <CartesianGrid
                  stroke={CHROME.gridline}
                  strokeWidth={1}
                  vertical={false}
                />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11, fill: CHROME.textMuted }}
                  tickLine={false}
                  axisLine={{ stroke: CHROME.axis }}
                  interval="preserveStartEnd"
                  minTickGap={12}
                />
                <YAxis
                  width={64}
                  tick={{ fontSize: 11, fill: CHROME.textMuted }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value: number) =>
                    displayCurrencyAmount(value, currency)
                  }
                />
                <Tooltip
                  content={<InvoicedTooltip currency={currency} />}
                  cursor={{ fill: "rgba(17,24,39,0.04)" }}
                />
                <Bar
                  dataKey="invoiced"
                  name="Invoiced"
                  fill={SERIES.primary}
                  maxBarSize={MARKS.maxBarThickness}
                  radius={MARKS.barRadius}
                  isAnimationActive={false}
                />
                <Bar
                  dataKey="collected"
                  name="Collected"
                  fill={SERIES.secondary}
                  maxBarSize={MARKS.maxBarThickness}
                  radius={MARKS.barRadius}
                  isAnimationActive={false}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-[#52514E]">
            <span className="inline-flex items-center gap-1.5">
              <span
                className="h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: SERIES.primary }}
                aria-hidden
              />
              Invoiced
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span
                className="h-2.5 w-2.5 rounded-sm"
                style={{ backgroundColor: SERIES.secondary }}
                aria-hidden
              />
              Collected
            </span>
            <span className="text-[#898781]">
              Recent periods collect less because those invoices are still
              within terms
            </span>
          </div>
        </>
      )}
    </article>
  );
}
